import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Button } from "./Button";

interface PromptPanelProps {
  prompt?: string;
  onPromptChange: (e: string) => void;
  isRegenerating: boolean;
  isInitialLoading: boolean;
  onRegenerateImages: () => void;
  uploadedFiles?: (File | null)[] | null;
  referenceImagePreviews?: string[] | null;
}

function useImagePreviews(
  uploadedFiles: (File | null)[] | null | undefined,
  referenceImagePreviews: string[] | null | undefined,
) {
  const [blobPreviews, setBlobPreviews] = useState<string[]>([]);

  useEffect(() => {
    const hasDataUrlPreviews =
      referenceImagePreviews && referenceImagePreviews.length > 0;
    const files = (uploadedFiles ?? []).filter(
      (file): file is File => file != null,
    );
    if (hasDataUrlPreviews || files.length === 0) {
      setBlobPreviews([]);
      return;
    }
    const urls = files.map((file) => URL.createObjectURL(file));
    setBlobPreviews(urls);
    return () => {
      for (const url of urls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [referenceImagePreviews, uploadedFiles]);

  return referenceImagePreviews?.length ? referenceImagePreviews : blobPreviews;
}

function ReferenceImagePreviews({
  imagePreviews,
  uploadedFiles,
}: {
  imagePreviews: string[];
  uploadedFiles?: (File | null)[] | null;
}) {
  if (imagePreviews.length === 0) return null;

  return (
    <div className="mb-6">
      <p className="text-label-field block mb-2">
        {imagePreviews.length > 1 ? "Reference Images" : "Reference Image"}
      </p>
      <div className="flex gap-2">
        {imagePreviews.map((preview, i) => (
          <div
            key={preview}
            className="flex-1 bg-[var(--bg-field)] rounded-lg border border-[var(--stroke-field)] overflow-hidden"
          >
            <img
              src={preview}
              alt={`Reference ${i + 1}`}
              className="w-full h-auto object-contain max-h-48"
            />
            {uploadedFiles?.[i] && (
              <p className="px-2 py-1 text-xs text-[var(--text-secondary)] truncate">
                {uploadedFiles[i].name}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PromptForm({
  prompt,
  onPromptChange,
  isRegenerating,
  isInitialLoading,
  onRegenerateImages,
}: {
  prompt?: string;
  onPromptChange: (e: string) => void;
  isRegenerating: boolean;
  isInitialLoading: boolean;
  onRegenerateImages: () => void;
}) {
  const isDisabled = isRegenerating || isInitialLoading;

  return (
    <div className="mb-6 bg-[var(--bg-modal)] rounded-xl p-6">
      <div className="text-body-default text-[var(--text-secondary)] mb-4">
        What would you change? Describe what you want to see differently below.
      </div>
      <textarea
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder="Enter your prompt..."
        className={`w-full bg-[var(--bg-field)] px-4 py-3 border rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent resize-none ${
          isDisabled
            ? "opacity-60 cursor-not-allowed border-[var(--stroke-field)]"
            : "border-[var(--stroke-field)]"
        }`}
        rows={6}
        disabled={isDisabled}
      />
      <Button
        onClick={onRegenerateImages}
        disabled={isDisabled}
        className="w-full mt-4"
      >
        {isRegenerating
          ? "Generating..."
          : isInitialLoading
            ? "Loading..."
            : "Generate 4 New Images"}
      </Button>
    </div>
  );
}

export function PromptPanel({
  prompt,
  onPromptChange,
  isRegenerating,
  isInitialLoading,
  onRegenerateImages,
  uploadedFiles,
  referenceImagePreviews,
}: PromptPanelProps) {
  const imagePreviews = useImagePreviews(uploadedFiles, referenceImagePreviews);

  useHotkeys(
    "Enter",
    (e) => {
      e.preventDefault();
      if (!(isRegenerating || isInitialLoading)) {
        onRegenerateImages();
      }
    },
    { enableOnFormTags: ["textarea"] },
  );

  return (
    <div className="w-80 flex flex-col">
      <div className="text-heading-modal font-normal text-[var(--text-primary)] mb-3">
        Image Selection
      </div>
      <div className="text-body-default text-[var(--text-secondary)] mb-8 leading-relaxed">
        Choose the image you want to refine for more control and precision, or
        add more information to your prompt to generate 4 new images.
      </div>
      <ReferenceImagePreviews
        imagePreviews={imagePreviews}
        uploadedFiles={uploadedFiles}
      />
      <PromptForm
        prompt={prompt}
        onPromptChange={onPromptChange}
        isRegenerating={isRegenerating}
        isInitialLoading={isInitialLoading}
        onRegenerateImages={onRegenerateImages}
      />
    </div>
  );
}
